import { FC, useRef, useContext } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import __ from "src/utils/Translations";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import createStructureIcon from "src/assets/custom/icons/create_structure.svg";
import useCreateStructure from "src/Pages/Company/OrganizationStructure/api/useCreateStructure";
import { StructureType } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/createStructureType";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { organizationStructureStructureCreate } from "src/utils/RoleBasedSecurity/permissions";
import { GlobalModalContext } from "src/utils/GlobalModal/GlobalModalContext";
import ImageUpload from "src/Components/ImageUpload/ImageUpload";
import { ImageCroppRef } from "src/Components/ImageCropp/ImageCropp";
import { styles } from "./createStructureStyle";
import {
  createStructureScheme,
  ICreateStructure,
} from "./CreateStructure.scheme";

const CreateStructure: FC = () => {
  const companyId = useCompanyId();
  const hasPermissionStructureCreate = usePermission(
    organizationStructureStructureCreate
  );

  const { mutate } = useCreateStructure(companyId);

  const formMethods = useForm<ICreateStructure>({
    resolver: standardSchemaResolver(createStructureScheme),
    defaultValues: {
      name: "",
      type: StructureType.CREATE_STRUCTURE_TYPE,
      avatarBase64: null,
    },
  });

  const { control, handleSubmit, setValue, reset } = formMethods;

  const handleResetModal = () => {
    reset();
    resetModal();
  };

  const onSubmit = (formData: ICreateStructure) => {
    const payload = {
      name: formData.name,
      type: StructureType.CREATE_STRUCTURE_TYPE,
      avatarBase64: formData.avatarBase64,
    };
    mutate(payload);
  };

  const { setModal, resetModal } = useContext(GlobalModalContext);
  const cropperRef = useRef<ImageCroppRef>(null);

  const modalActions: {
    label: string;
    variant?: "outlined" | "text" | "contained";
    callbackFn: VoidFunction;
  }[] = [
    {
      label: __("Anuluj") as string,
      variant: "outlined",
      callbackFn: handleResetModal,
    },
    {
      label: __("Dodaj") as string,
      variant: "contained",
      callbackFn: async () => {
        await cropperRef.current?.submitCrop();
        await handleSubmit(onSubmit)();
        resetModal();
      },
    },
  ];

  return (
    <FormProvider {...formMethods}>
      <Box>
        <Typography
          variant="h4"
          fontWeight="fontWeightBold"
          textTransform="uppercase"
          mb="32px"
        >
          {`${__("Tworzenie struktury")}`}
        </Typography>
        <Box sx={styles.container}>
          <Box sx={styles.iconContainer}>
            <img src={createStructureIcon} alt={__("tworzenie struktury")} />
          </Box>
          <Typography sx={styles.title}>
            {__("Stwórz strukturę dla firmy")}
          </Typography>
          <Typography sx={styles.description}>
            {__(
              "Struktura będzie obejmować wszystkich pracowników w Twojej firmie."
            )}
          </Typography>

          {hasPermissionStructureCreate && (
            <Button
              variant="contained"
              size="large"
              type="button"
              onClick={() => {
                setModal({
                  open: true,
                  title: (
                    <Typography sx={styles.dialogTitle} component="h2">
                      {__("Nowa struktura")}
                    </Typography>
                  ),
                  description: __(
                    "Struktura będzie widoczna dla wszystkich pracowników w Twojej organizacji."
                  ),
                  customElements: (
                    <>
                      <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            label={__("Nazwa")}
                            helperText={
                              error
                                ? __(error.message)
                                : __(
                                    "Tak będzie się nazywał startowy element w Twojej strukturze."
                                  )
                            }
                            error={!!error}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography variant="subtitle2" mt={3} mb={2}>
                          {__("Avatar struktury")}:
                        </Typography>
                        <ImageUpload
                          ref={cropperRef}
                          onImageCropped={(img) => {
                            const result =
                              typeof img === "string"
                                ? img
                                : URL.createObjectURL(img);
                            setValue("avatarBase64", result);
                          }}
                        />
                      </Box>
                    </>
                  ),
                  actions: modalActions,
                  maxWidth: 800,
                  disableExitButton: true,
                  disableOutsideClick: true,
                });
              }}
            >
              {__("Zaczynamy")}
            </Button>
          )}
        </Box>
      </Box>
    </FormProvider>
  );
};

export default CreateStructure;
